import { useState, useEffect, useRef } from 'react';
import { useList, useNavigation, useApiUrl } from '@refinedev/core';
import { useSearchParams } from 'react-router-dom';
import { formatDate, formatDateOrNever, resolveUserDisplayName } from '@/lib/utils';
import { HelpTooltip } from '@/components/ui/Tooltip';
import { UserRowSkeleton } from '@/components/ui/Skeleton';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { toast } from '@/hooks/useToast';
import { adminFetch } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { InviteAppUserModal } from './InviteAppUserModal';
import { SortableHeader, nextSort, type SortOrder } from '@/components/table/SortableHeader';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Eye,
  MoreHorizontal,
  UserX,
  UserCheck,
  Trash2,
  Check,
  AlertTriangle,
  Filter,
  X,
  Calendar,
  UserPlus,
} from 'lucide-react';

interface User {
  id: string;
  email: string;
  display_name: string;
  first_name: string;
  last_name: string;
  status: 'active' | 'suspended' | 'deleted';
  created_at: string;
  last_active_at: string | null;
  photos_count: number;
  matches_count: number;
  is_fake?: boolean;
  is_tester?: boolean;
}

const PAGE_SIZE_OPTIONS = [15, 20, 50, 100];

export function UserListPage() {
  const { show } = useNavigation();
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  // Invite-app-user modal state (Topic 4 PR-2). Permission-gated trigger:
  // the button is hidden if the admin doesn't hold `users.create`, so
  // most admins (e.g. Support) never see this surface. Backend re-checks
  // on POST /v1/admin/app-users so a permission revoked between page
  // load and submit still fails closed (modal surfaces the 403).
  const [showInviteAppUser, setShowInviteAppUser] = useState(false);

  // Initialize state from URL params (enables drill-down from dashboard)
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || 'all');
  const [userTypeFilter, setUserTypeFilter] = useState<string>(searchParams.get('userType') || 'all');
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('pageSize')) || 20);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [jumpToPage, setJumpToPage] = useState('');

  // Sort state (server-side via the data provider → sort_by/sort_order).
  const [sortField, setSortField] = useState<string | null>(searchParams.get('sort') || null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    searchParams.get('order') === 'asc' ? 'asc' : 'desc'
  );

  const handleSort = (field: string) => {
    const next = nextSort({ field: sortField, order: sortOrder }, field);
    setSortField(next.field);
    setSortOrder(next.order);
    setCurrentPage(1);
  };

  // Advanced filters - initialize from URL params
  const initialJoinedFrom = searchParams.get('joinedFrom') || '';
  const initialJoinedTo = searchParams.get('joinedTo') || '';
  const initialLastActiveFrom = searchParams.get('lastActiveFrom') || '';
  const initialLastActiveTo = searchParams.get('lastActiveTo') || '';
  const initialGender = searchParams.get('gender') || '';
  const initialOnboarding = searchParams.get('onboarding') || '';
  const initialHasPhotos = searchParams.get('hasPhotos') || '';

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(
    !!(
      initialJoinedFrom ||
      initialJoinedTo ||
      initialLastActiveFrom ||
      initialLastActiveTo ||
      initialGender ||
      initialOnboarding ||
      initialHasPhotos
    )
  );
  const [joinedFrom, setJoinedFrom] = useState(initialJoinedFrom);
  const [joinedTo, setJoinedTo] = useState(initialJoinedTo);
  const [lastActiveFrom, setLastActiveFrom] = useState(initialLastActiveFrom);
  const [lastActiveTo, setLastActiveTo] = useState(initialLastActiveTo);
  const [genderFilter, setGenderFilter] = useState(initialGender);
  const [onboardingFilter, setOnboardingFilter] = useState(initialOnboarding);
  const [hasPhotosFilter, setHasPhotosFilter] = useState(initialHasPhotos);

  // Sync URL with filter state (makes links shareable)
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (userTypeFilter !== 'all') params.set('userType', userTypeFilter);
    if (currentPage > 1) params.set('page', String(currentPage));
    if (pageSize !== 20) params.set('pageSize', String(pageSize));
    if (joinedFrom) params.set('joinedFrom', joinedFrom);
    if (joinedTo) params.set('joinedTo', joinedTo);
    if (lastActiveFrom) params.set('lastActiveFrom', lastActiveFrom);
    if (lastActiveTo) params.set('lastActiveTo', lastActiveTo);
    if (genderFilter) params.set('gender', genderFilter);
    if (onboardingFilter) params.set('onboarding', onboardingFilter);
    if (hasPhotosFilter) params.set('hasPhotos', hasPhotosFilter);
    if (sortField) {
      params.set('sort', sortField);
      params.set('order', sortOrder);
    }

    // Only update if params changed to avoid unnecessary re-renders
    const newParamsString = params.toString();
    const currentParamsString = searchParams.toString();
    if (newParamsString !== currentParamsString) {
      setSearchParams(params, { replace: true });
    }
  }, [search, statusFilter, userTypeFilter, currentPage, pageSize, joinedFrom, joinedTo, lastActiveFrom, lastActiveTo, genderFilter, onboardingFilter, hasPhotosFilter, sortField, sortOrder, setSearchParams, searchParams]);

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showBulkSuspendModal, setShowBulkSuspendModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkActionError, setBulkActionError] = useState<string | null>(null);
  const [bulkDeleteConfirmed, setBulkDeleteConfirmed] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data, isLoading, isError, refetch } = useList<User>({
    resource: 'users',
    pagination: {
      current: currentPage,
      pageSize,
    },
    filters: [
      ...(search ? [{ field: 'search', operator: 'contains' as const, value: search }] : []),
      ...(statusFilter !== 'all' ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
      ...(userTypeFilter !== 'all' ? [{ field: 'user_type', operator: 'eq' as const, value: userTypeFilter }] : []),
      ...(joinedFrom ? [{ field: 'created_at_gte', operator: 'eq' as const, value: joinedFrom }] : []),
      ...(joinedTo ? [{ field: 'created_at_lte', operator: 'eq' as const, value: joinedTo }] : []),
      ...(lastActiveFrom ? [{ field: 'last_active_at_gte', operator: 'eq' as const, value: lastActiveFrom }] : []),
      ...(lastActiveTo ? [{ field: 'last_active_at_lte', operator: 'eq' as const, value: lastActiveTo }] : []),
      ...(genderFilter ? [{ field: 'gender', operator: 'eq' as const, value: genderFilter }] : []),
      ...(onboardingFilter ? [{ field: 'onboarding', operator: 'eq' as const, value: onboardingFilter }] : []),
      ...(hasPhotosFilter ? [{ field: 'has_photos', operator: 'eq' as const, value: hasPhotosFilter }] : []),
    ],
    sorters: sortField ? [{ field: sortField, order: sortOrder }] : [],
  });

  // Count active filters
  const activeFilterCount = [
    joinedFrom,
    joinedTo,
    lastActiveFrom,
    lastActiveTo,
    genderFilter,
    onboardingFilter,
    hasPhotosFilter,
  ].filter(Boolean).length;

  const users = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  // Clear selections when page/filters change
  useEffect(() => {
    setSelectedUsers(new Set());
  }, [currentPage, pageSize, search, statusFilter, userTypeFilter, joinedFrom, joinedTo, lastActiveFrom, lastActiveTo, genderFilter, onboardingFilter, hasPhotosFilter, sortField, sortOrder]);

  const clearAdvancedFilters = () => {
    setJoinedFrom('');
    setJoinedTo('');
    setLastActiveFrom('');
    setLastActiveTo('');
    setGenderFilter('');
    setOnboardingFilter('');
    setHasPhotosFilter('');
    setCurrentPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleJumpToPage = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(jumpToPage, 10);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setJumpToPage('');
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map((u) => u.id)));
    }
  };

  const toggleSelectUser = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const selectedUsersData = users.filter((u) => selectedUsers.has(u.id));
  const canBulkSuspend = selectedUsersData.some((u) => u.status === 'active');
  const canBulkDelete = selectedUsersData.some((u) => u.status !== 'deleted');

  // Bulk action handlers
  const handleBulkSuspend = async (reason: string) => {
    setBulkActionLoading(true);
    setBulkActionError(null);
    try {
      const usersToSuspend = selectedUsersData.filter((u) => u.status === 'active');
      for (const user of usersToSuspend) {
        try {
          await adminFetch(`${apiUrl}/v1/admin/users/${user.id}/suspend`, {
            method: 'POST',
            body: JSON.stringify({ reason }),
          });
        } catch (err) {
          throw new Error(
            `Failed to suspend user ${user.email}: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      }
      setShowBulkSuspendModal(false);
      setSelectedUsers(new Set());
      await refetch();
      // Show success toast
      toast({
        title: 'Users Suspended',
        description: `Successfully suspended ${usersToSuspend.length} user(s)`,
        variant: 'warning',
      });
    } catch (err) {
      setBulkActionError(err instanceof Error ? err.message : 'Failed to suspend users');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async (reason: string) => {
    setBulkActionLoading(true);
    setBulkActionError(null);
    try {
      const usersToDelete = selectedUsersData.filter((u) => u.status !== 'deleted');
      for (const user of usersToDelete) {
        try {
          await adminFetch(`${apiUrl}/v1/admin/users/${user.id}`, {
            method: 'DELETE',
            body: JSON.stringify({ reason }),
          });
        } catch (err) {
          throw new Error(
            `Failed to delete user ${user.email}: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      }
      setShowBulkDeleteModal(false);
      setBulkDeleteConfirmed(false);
      setSelectedUsers(new Set());
      await refetch();
      // Show success toast
      toast({
        title: 'Users Deleted',
        description: `Successfully deleted ${usersToDelete.length} user(s)`,
        variant: 'destructive',
      });
    } catch (err) {
      setBulkActionError(err instanceof Error ? err.message : 'Failed to delete users');
    } finally {
      setBulkActionLoading(false);
    }
  };

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
        Failed to load users. Please try again.
      </div>
    );
  }

  // We'll show skeleton in the table body instead of returning early

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Users</h2>
          <p className="text-muted-foreground">Manage user accounts and view user details</p>
        </div>
        {hasPermission('users.create') && (
          <button
            type="button"
            onClick={() => setShowInviteAppUser(true)}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <UserPlus className="h-4 w-4" />
            Invite app user
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 w-64 rounded-lg border bg-background pl-10 pr-8 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <HelpTooltip content="Search by user's display name, first name, last name, or email address" />
            </div>
          </div>
          <button
            type="submit"
            className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Search
          </button>
        </form>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-sm text-muted-foreground">
              Status:
              <HelpTooltip content="Active: Can log in and use the app. Suspended: Temporarily blocked from logging in. Deleted: Account permanently removed." />
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              aria-label="Filter by status"
              className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-sm text-muted-foreground">
              Type:
              <HelpTooltip content="Real: organic app users. Fake: synthetic matching-pool users (tailored = generated for a tester's filters; seeded = demographic pool). Tester: flagged engineer/QA accounts." />
            </label>
            <select
              value={userTypeFilter}
              onChange={(e) => {
                setUserTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              aria-label="Filter by user type"
              className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="all">All types</option>
              <option value="real">Real</option>
              <option value="fake">Fake (all)</option>
              <option value="fake_tailored">Fake-tailored</option>
              <option value="fake_seeded">Fake-seeded</option>
              <option value="tester">Tester</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Show:</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              aria-label="Items per page"
              className="h-10 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* Advanced Filters Toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`flex items-center gap-2 h-10 rounded-lg border px-3 text-sm transition-colors ${
              showAdvancedFilters || activeFilterCount > 0
                ? 'border-primary bg-primary/5 text-primary'
                : 'hover:bg-muted'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Advanced Filters
              <HelpTooltip content="Filter users by date ranges. All filters can be combined with search and status filter for more precise results." />
            </h3>
            {activeFilterCount > 0 && (
              <button
                onClick={clearAdvancedFilters}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
                Clear all
              </button>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Joined Date Range */}
            <div>
              <label htmlFor="joined-from" className="text-sm text-muted-foreground">Joined From</label>
              <input
                id="joined-from"
                type="date"
                value={joinedFrom}
                onChange={(e) => {
                  setJoinedFrom(e.target.value);
                  setCurrentPage(1);
                }}
                className="mt-1 h-9 w-full rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="joined-to" className="text-sm text-muted-foreground">Joined To</label>
              <input
                id="joined-to"
                type="date"
                value={joinedTo}
                onChange={(e) => {
                  setJoinedTo(e.target.value);
                  setCurrentPage(1);
                }}
                className="mt-1 h-9 w-full rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {/* Last Active Date Range */}
            <div>
              <label htmlFor="last-active-from" className="text-sm text-muted-foreground">Last Active From</label>
              <input
                id="last-active-from"
                type="date"
                value={lastActiveFrom}
                onChange={(e) => {
                  setLastActiveFrom(e.target.value);
                  setCurrentPage(1);
                }}
                className="mt-1 h-9 w-full rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="last-active-to" className="text-sm text-muted-foreground">Last Active To</label>
              <input
                id="last-active-to"
                type="date"
                value={lastActiveTo}
                onChange={(e) => {
                  setLastActiveTo(e.target.value);
                  setCurrentPage(1);
                }}
                className="mt-1 h-9 w-full rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {/* Gender */}
            <div>
              <label htmlFor="gender-filter" className="text-sm text-muted-foreground">Gender</label>
              <select
                id="gender-filter"
                value={genderFilter}
                onChange={(e) => {
                  setGenderFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="mt-1 h-9 w-full rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Any</option>
                <option value="woman">Woman</option>
                <option value="man">Man</option>
                <option value="non_binary">Non-binary</option>
                <option value="transgender_woman">Transgender woman</option>
                <option value="transgender_man">Transgender man</option>
                <option value="genderfluid">Genderfluid</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Onboarding */}
            <div>
              <label htmlFor="onboarding-filter" className="text-sm text-muted-foreground">Onboarding</label>
              <select
                id="onboarding-filter"
                value={onboardingFilter}
                onChange={(e) => {
                  setOnboardingFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="mt-1 h-9 w-full rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Any</option>
                <option value="complete">Complete</option>
                <option value="incomplete">Incomplete</option>
              </select>
            </div>

            {/* Has photos */}
            <div>
              <label htmlFor="has-photos-filter" className="text-sm text-muted-foreground">Photos</label>
              <select
                id="has-photos-filter"
                value={hasPhotosFilter}
                onChange={(e) => {
                  setHasPhotosFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="mt-1 h-9 w-full rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="">Any</option>
                <option value="true">Has photos</option>
                <option value="false">No photos</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Active Filter Chips */}
      {(statusFilter !== 'all' || search || activeFilterCount > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {search && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm">
              Search: "{search}"
              <button
                onClick={() => setSearch('')}
                aria-label="Clear search filter"
                className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {statusFilter !== 'all' && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm">
              Status: {statusFilter}
              <button
                onClick={() => setStatusFilter('all')}
                aria-label="Clear status filter"
                className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {joinedFrom && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm">
              Joined from: {joinedFrom}
              <button
                onClick={() => setJoinedFrom('')}
                aria-label="Clear joined from filter"
                className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {joinedTo && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm">
              Joined to: {joinedTo}
              <button
                onClick={() => setJoinedTo('')}
                aria-label="Clear joined to filter"
                className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {lastActiveFrom && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm">
              Last active from: {lastActiveFrom}
              <button
                onClick={() => setLastActiveFrom('')}
                aria-label="Clear last active from filter"
                className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {lastActiveTo && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm">
              Last active to: {lastActiveTo}
              <button
                onClick={() => setLastActiveTo('')}
                aria-label="Clear last active to filter"
                className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {genderFilter && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm">
              Gender: {genderFilter}
              <button
                onClick={() => setGenderFilter('')}
                aria-label="Clear gender filter"
                className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {onboardingFilter && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm">
              Onboarding: {onboardingFilter}
              <button
                onClick={() => setOnboardingFilter('')}
                aria-label="Clear onboarding filter"
                className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {hasPhotosFilter && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm">
              {hasPhotosFilter === 'true' ? 'Has photos' : 'No photos'}
              <button
                onClick={() => setHasPhotosFilter('')}
                aria-label="Clear photos filter"
                className="ml-1 rounded-full hover:bg-primary/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedUsers.size > 0 && (
        <div className="flex items-center justify-between rounded-lg border bg-muted/50 px-4 py-3">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">{selectedUsers.size} user(s) selected</span>
            <button
              onClick={() => setSelectedUsers(new Set())}
              className="ml-2 text-sm text-muted-foreground hover:text-foreground"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            {canBulkSuspend && (
              <button
                onClick={() => setShowBulkSuspendModal(true)}
                className="flex items-center gap-2 rounded-lg border border-yellow-500 px-3 py-1.5 text-sm font-medium text-yellow-600 hover:bg-yellow-50"
              >
                <UserX className="h-4 w-4" />
                Suspend Selected
              </button>
            )}
            {canBulkDelete && (
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                className="flex items-center gap-2 rounded-lg border border-destructive px-3 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
                Delete Selected
              </button>
            )}
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={users.length > 0 && selectedUsers.size === users.length}
                    onChange={toggleSelectAll}
                    aria-label="Select all users"
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                </th>
                <SortableHeader
                  field="display_name"
                  label="User"
                  activeField={sortField}
                  activeOrder={sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="status"
                  label="Status"
                  activeField={sortField}
                  activeOrder={sortOrder}
                  onSort={handleSort}
                  trailing={<HelpTooltip content="Active (green), Suspended (yellow), or Deleted (red)" side="bottom" />}
                />
                <SortableHeader
                  field="created_at"
                  label="Joined"
                  activeField={sortField}
                  activeOrder={sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="last_active_at"
                  label="Last Active"
                  activeField={sortField}
                  activeOrder={sortOrder}
                  onSort={handleSort}
                  trailing={<HelpTooltip content="Shows 'Never' if the user hasn't logged in since account creation. Shows '—' if data is unavailable." side="bottom" />}
                />
                <SortableHeader
                  field="photos_count"
                  label="Photos"
                  activeField={sortField}
                  activeOrder={sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="matches_count"
                  label="Matches"
                  activeField={sortField}
                  activeOrder={sortOrder}
                  onSort={handleSort}
                />
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                // Show skeleton loading rows
                Array.from({ length: pageSize }).map((_, index) => (
                  <UserRowSkeleton key={`skeleton-${index}`} />
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className={`border-b last:border-0 hover:bg-muted/30 ${selectedUsers.has(user.id) ? 'bg-primary/5' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(user.id)}
                        onChange={() => toggleSelectUser(user.id)}
                        aria-label="Select user"
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="flex items-center gap-1.5 font-medium">
                          <span>{resolveUserDisplayName(user)}</span>
                          {user.is_fake && (
                            <span className="rounded bg-violet-600 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
                              {user.email?.startsWith('fake-tailored-')
                                ? 'tailored'
                                : user.email?.startsWith('fake-seeded-')
                                  ? 'seeded'
                                  : 'fake'}
                            </span>
                          )}
                          {user.is_tester && !user.is_fake && (
                            <span className="rounded bg-sky-500 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-white">
                              tester
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">{user.email}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={user.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(user.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDateOrNever(user.last_active_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">{user.photos_count}</td>
                    <td className="px-4 py-3 text-sm">{user.matches_count}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => show('users', user.id)}
                          className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                          title="View details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <div className="relative" ref={openMenuId === user.id ? menuRef : null}>
                          <button
                            onClick={() => setOpenMenuId(openMenuId === user.id ? null : user.id)}
                            aria-label="More actions"
                            className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                          {openMenuId === user.id && (
                            <div className="absolute right-0 top-full z-10 mt-1 w-48 rounded-lg border bg-card py-1 shadow-lg">
                              {user.status === 'active' && (
                                <button
                                  onClick={() => {
                                    show('users', user.id);
                                    setOpenMenuId(null);
                                  }}
                                  className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-muted"
                                >
                                  <UserX className="h-4 w-4" />
                                  Suspend User
                                </button>
                              )}
                              {user.status === 'suspended' && (
                                <button
                                  onClick={() => {
                                    show('users', user.id);
                                    setOpenMenuId(null);
                                  }}
                                  className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-muted"
                                >
                                  <UserCheck className="h-4 w-4" />
                                  Unsuspend User
                                </button>
                              )}
                              {user.status !== 'deleted' && (
                                <button
                                  onClick={() => {
                                    show('users', user.id);
                                    setOpenMenuId(null);
                                  }}
                                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-destructive hover:bg-muted"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Delete User
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Enhanced Pagination */}
        {totalPages > 0 && (
          <div className="flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {total === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{' '}
              {Math.min(currentPage * pageSize, total)} of {total} users
            </div>

            <div className="flex flex-wrap items-center gap-4">
              {/* Jump to page */}
              <form onSubmit={handleJumpToPage} className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Go to:</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={jumpToPage}
                  onChange={(e) => setJumpToPage(e.target.value)}
                  placeholder={String(currentPage)}
                  className="h-8 w-16 rounded-lg border bg-background px-2 text-sm text-center focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <button
                  type="submit"
                  disabled={!jumpToPage}
                  className="h-8 rounded-lg bg-muted px-2 text-sm font-medium hover:bg-muted/80 disabled:opacity-50"
                >
                  Go
                </button>
              </form>

              {/* Page navigation */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                  title="First page"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                  title="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-sm">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                  title="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                  title="Last page"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Suspend Modal */}
      {showBulkSuspendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100">
                <UserX className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Suspend Users</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedUsersData.filter((u) => u.status === 'active').length} user(s) will be suspended
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              This will prevent the selected users from logging in and using the platform.
            </p>

            {bulkActionError && (
              <div className="rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive mb-4">
                {bulkActionError}
              </div>
            )}

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                await handleBulkSuspend(formData.get('reason') as string);
              }}
              className="space-y-4"
            >
              <div>
                <label className="text-sm font-medium">Reason *</label>
                <textarea
                  name="reason"
                  required
                  rows={3}
                  disabled={bulkActionLoading}
                  className="mt-1 w-full rounded-lg border bg-background p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                  placeholder="Enter suspension reason for all selected users..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkSuspendModal(false);
                    setBulkActionError(null);
                  }}
                  disabled={bulkActionLoading}
                  className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bulkActionLoading}
                  className="flex items-center gap-2 rounded-lg bg-yellow-500 px-4 py-2 text-sm font-medium text-white hover:bg-yellow-600 disabled:opacity-50"
                >
                  {bulkActionLoading && (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  )}
                  Suspend Users
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-destructive">Delete Users</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedUsersData.filter((u) => u.status !== 'deleted').length} user(s) will be deleted
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3 mb-4">
              <p className="text-sm text-destructive font-medium">This action is permanent and cannot be undone.</p>
              <p className="text-sm text-muted-foreground mt-1">
                All user data, photos, matches, and messages will be permanently removed.
              </p>
            </div>

            {bulkActionError && (
              <div className="rounded-lg border border-destructive bg-destructive/10 p-3 text-sm text-destructive mb-4">
                {bulkActionError}
              </div>
            )}

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                await handleBulkDelete(formData.get('reason') as string);
              }}
              className="space-y-4"
            >
              <div>
                <label className="text-sm font-medium">Reason for deletion *</label>
                <textarea
                  name="reason"
                  required
                  rows={3}
                  disabled={bulkActionLoading}
                  className="mt-1 w-full rounded-lg border bg-background p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                  placeholder="Enter deletion reason for all selected users..."
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkDeleteConfirmed}
                  onChange={(e) => setBulkDeleteConfirmed(e.target.checked)}
                  disabled={bulkActionLoading}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-destructive focus:ring-destructive"
                />
                <span className="text-sm text-muted-foreground">
                  I understand that this action is <strong className="text-foreground">permanent</strong> and cannot be
                  undone.
                </span>
              </label>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkDeleteModal(false);
                    setBulkDeleteConfirmed(false);
                    setBulkActionError(null);
                  }}
                  disabled={bulkActionLoading}
                  className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!bulkDeleteConfirmed || bulkActionLoading}
                  className="flex items-center gap-2 rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkActionLoading && (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  )}
                  <Trash2 className="h-4 w-4" />
                  Delete Users
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invite app user modal (Topic 4 PR-2) */}
      <InviteAppUserModal
        open={showInviteAppUser}
        onClose={(created) => {
          setShowInviteAppUser(false);
          if (created) {
            toast({
              title: 'Invite sent',
              description: `Welcome email queued for ${created.email}.`,
              variant: 'success',
            });
            // Navigate to the new user's detail page so the inviter can
            // immediately verify the result + see the admin-invited chip.
            show('users', created.user_id);
          }
        }}
      />
    </div>
  );
}
